'use client'

import { useEffect } from 'react'

/**
 * Member-wide authenticated-activity beacon.
 *
 * Fires a lightweight, fire-and-forget signal to /api/surface-views (surface
 * 'member_web') when a LOGGED-IN member lands on any member-facing web surface —
 * account, content detail, tutorials, the course/lesson viewer, community, etc.
 * This is what feeds the engagement-based DAU/WAU/MAU union (via surface_views):
 * login_events only captured credential sign-ins, which badly undercounts members
 * who stay signed in for weeks.
 *
 * Contract:
 *  - Authenticated-only: `enabled` is the server-resolved "is this a logged-in
 *    member?" flag; when false we never touch the network, so anonymous public
 *    traffic costs nothing and the API no-ops anyway (no user → no row).
 *  - Deduped per surface per UTC calendar day (localStorage) — one ping per member
 *    per day is all DAU/WAU/MAU needs, which keeps writes minimal.
 *  - Non-blocking: navigator.sendBeacon (keepalive by design), falling back to a
 *    keepalive fetch; both send the session cookie same-origin so the server can
 *    attribute the user.
 *  - Prefetch-safe: useEffect runs only after hydration in a real browser, never
 *    during an RSC prefetch (a prefetch fetches the payload but does not mount or
 *    hydrate client components), so this inherently respects the prefetch guards.
 *    Bots that don't execute JS never reach here, and the route adds a
 *    prefetch-header + bot-UA backstop.
 *
 * Renders nothing.
 */
export function MemberActivityPing({
  enabled,
  surface = 'member_web',
}: {
  enabled: boolean
  surface?: string
}) {
  useEffect(() => {
    if (!enabled) return
    const today = new Date().toISOString().slice(0, 10) // UTC day
    const key = `theduchy.member-activity.${surface}.${today}`
    try {
      if (localStorage.getItem(key)) return
      localStorage.setItem(key, '1')
    } catch {
      // Storage blocked (private mode / policy) — fall through and ping once per
      // mount; the server's per-user rate limit bounds the worst case.
    }

    const body = JSON.stringify({ surface })
    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' })
        if (navigator.sendBeacon('/api/surface-views', blob)) return
      }
    } catch {
      // sendBeacon unavailable/failed — fall back to keepalive fetch.
    }
    fetch('/api/surface-views', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {})
  }, [enabled, surface])

  return null
}
